/**
 * QR 코드 스캐너 컴포넌트
 */

import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/library';
import './QRScanner.css';
import { extractQRCode } from '../utils/qrUtils';

// facingMode: 'environment'(기본) = 후면 카메라, 'user' = 화면 쪽 전면 카메라.
//   - 태블릿 현장 스캔(QrSticker/AllInOne/JrMember 등)은 후면 기본 유지.
//   - 광장 TV(QuestStatusPage)처럼 화면을 마주 보고 찍는 경우 'user' 전달.
// onInvalidScan: 파서(extractQRCode) 가 null 을 돌려준 스캔의 선택 콜백(D9). 미전달 소비처는 동작 불변.
// extract: 스캔 원문 → 코드 추출 함수(선택). **기본값이 기존 extractQRCode** 라 미전달 소비처는
//   동작이 한 줄도 바뀌지 않습니다. 여권취소 화면만 `extractPassportQRCode` 를 주입해
//   `ZLP_` 를 읽습니다 — 공유 파서를 넓히면 이 컴포넌트를 쓰는 다른 화면들이
//   여권 QR 을 서버로 보내기 시작하기 때문입니다(기획서 §4.12.4 C19).
const QRScanner = ({
  onScan, onError, onInvalidScan, enabled = true, facingMode = 'environment',
  extract = extractQRCode,
  decodeAttemptIntervalMs = 0,
}) => {
  const videoRef = useRef(null);
  const [reader] = useState(() => new BrowserMultiFormatReader());
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const restartTimerRef = useRef(null);

  // Only opt-in consumers throttle unsuccessful camera decode attempts.
  // ZXing defaults to 0ms; keep that behavior for existing consumers.
  useEffect(() => {
    reader.timeBetweenDecodingAttempts = decodeAttemptIntervalMs;
  }, [reader, decodeAttemptIntervalMs]);

  // HTTP 환경(개발서버)에서는 카메라 사용 불가
  const isDevMode = window.location.protocol === 'http:' && window.location.hostname !== 'localhost';

  useEffect(() => {
    if (!enabled || isDevMode) {
      stopScanning();
      return;
    }

    startScanning();

    return () => {
      stopScanning();
    };
  }, [enabled, isDevMode]);

  // USB 장치 연결/해제 시 카메라 스트림 자동 복구
  useEffect(() => {
    const handleDeviceChange = () => {
      console.log('[QRScanner] Device change detected');
      if (!enabled) return;

      // 현재 스트림이 살아있는지 확인
      const tracks = videoRef.current?.srcObject?.getVideoTracks() || [];
      const alive = tracks.some((t) => t.readyState === 'live');
      if (alive) return;

      // 스트림이 죽었으면 debounce 후 재시작 (장치 안정화 대기)
      console.log('[QRScanner] Camera stream lost, restarting...');
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
      restartTimerRef.current = setTimeout(() => {
        stopScanning();
        startScanning();
        restartTimerRef.current = null;
      }, 500);
    };

    if (!navigator.mediaDevices) return;
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      if (restartTimerRef.current) {
        clearTimeout(restartTimerRef.current);
        restartTimerRef.current = null;
      }
    };
  }, [enabled]);

  const startScanning = async () => {
    try {
      setError(null);

      // HTTPS가 아닌 환경에서는 mediaDevices API 사용 불가
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw { name: 'NotAllowedError' };
      }

      // 카메라 권한 요청
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
      });

      // 비디오 엘리먼트에 스트림 연결
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // QR 코드 스캔 시작 (기존 스트림 사용)
      reader.decodeFromStream(stream, videoRef.current, (result, err) => {
        if (result) {
          const qrCode = result.getText();
          console.log('[QRScanner] Scanned:', qrCode);

          // QR 코드 추출 (URL 형태 또는 raw 코드 모두 지원)
          // ⚠️ 이 콜백은 등록 시점의 클로저를 계속 씁니다(onScan 과 동일한 성질).
          //    `extract` 는 **순수 파서**라 그 값이 고정돼도 결과가 달라지지 않습니다 —
          //    소비처는 모듈 상수 함수를 넘기십시오(인라인 화살표는 불필요합니다).
          const extracted = extract(qrCode);
          if (extracted) {
            onScan(extracted);
          } else {
            console.warn('[QRScanner] Invalid QR format:', qrCode);
            if (onInvalidScan) onInvalidScan(qrCode); // D9 — 무음 실패 가시화(선택 콜백)
          }
        }
        if (err && err.name !== 'NotFoundException') {
          console.error('[QRScanner] Error:', err);
        }
      });

      setIsScanning(true);
    } catch (err) {
      console.error('[QRScanner] Failed to start:', err);
      setError(getCameraErrorMessage(err));
      if (onError) {
        onError(err);
      }
    }
  };

  const stopScanning = () => {
    reader.reset();
    setIsScanning(false);

    // 비디오 스트림 정지
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const getCameraErrorMessage = (err) => {
    if (err.name === 'NotAllowedError') {
      return '카메라 접근 권한이 필요합니다. 브라우저 설정에서 카메라 권한을 허용해주세요.';
    }
    if (err.name === 'NotFoundError') {
      return '카메라를 찾을 수 없습니다. 카메라가 연결되어 있는지 확인해주세요.';
    }
    if (err.name === 'NotReadableError') {
      return '카메라가 다른 앱에서 사용 중입니다.';
    }
    if (!navigator.mediaDevices) {
      return '카메라를 사용하려면 HTTPS 연결이 필요합니다.';
    }
    return '카메라를 시작할 수 없습니다.';
  };

  // 카메라 불가 = 카메라 없는 기기(안드로이드 TV·프로젝터 등, getUserMedia 실패→error)
  //   또는 개발 HTTP(isDevMode). 이 경우 에러 화면 대신 "대기중" 한 줄만 보여준다.
  //   USB QR 리더 입력은 각 페이지의 useUsbQrReader 훅이 그대로 처리하므로 별도 입력 UI 는 두지 않는다.
  const cameraUnavailable = isDevMode || Boolean(error);

  return (
    <div className="qr-scanner">
      {cameraUnavailable ? (
        <div className="qr-scanner-standby">
          <p className="qr-scanner-standby-title">QR 스캐너 대기중</p>
        </div>
      ) : (
        <>
          <div className="qr-scanner-viewport">
            <video ref={videoRef} autoPlay playsInline muted />
            <div className="qr-scanner-overlay">
              <div className="qr-scanner-frame" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QRScanner;
